/**
 * ⟦C21 · A-146 §2⟧ `useMattChambre` — la surface qui fait parler Matt dans la chambre.
 *
 * Remplace `ModeDecouverte` (le voile bloquant) et `InfoBulleDecouverte` (la bulle ancrée) pour
 * cet écran précis : les deux composants restent montés ailleurs (`App.tsx` les retire
 * seulement de la chambre), et continuent de fonctionner à l'identique sur les écrans qui n'ont
 * pas Matt en hôte.
 *
 * Le guetteur reprend le même patron que les deux composants qu'il remplace (synchroniser la
 * mémoire par run, demander la prochaine leçon due tant que « libre », un `pointerdown` n'est
 * plus nécessaire — la boîte ne se ferme plus au clic à côté, direction F3 : elle se ferme au
 * ▶, jamais ailleurs). `game/parole.ts` décide QUOI et DANS QUEL ORDRE (bloquante > bulle >
 * petit écran) ; ce hook ne fait que le brancher sur les stores et gérer l'avancée réplique par
 * réplique, un état purement local qui n'a pas sa place dans une fonction pure.
 */

import { useEffect, useRef, useState } from "react";

import { modaleEvenementOuverte, repliquesDe, type LeconId } from "../../game/decouverte";
import {
  haloDeReplique,
  paroleDue,
  paroleNonBloquanteAffichee,
  ID_APRES_ACCUEIL,
  ID_PETIT_ECRAN,
  REPLIQUE_APRES_ACCUEIL,
  REPLIQUE_PETIT_ECRAN,
} from "../../game/parole";
import { useAccueilStore } from "../../stores/accueilStore";
import { useDecouverteStore } from "../../stores/decouverteStore";
import { useIntroStore } from "../../stores/introStore";
import { useRunStore } from "../../stores/runStore";
import { useUIStore } from "../../stores/uiStore";
import { useSfx } from "../../components/SoundProvider";
import { REQUETE_PETIT_ECRAN } from "../../components/BandeauPetitEcran";

export interface MattChambre {
  /** Une réplique est à l'écran. */
  actif: boolean;
  /** Cette réplique bloque les actions flottantes (`bloquante: true` de `game/decouverte.ts`). */
  bloquante: boolean;
  texte: string;
  /** L'id à pulser en halo (`Studio.tsx` compare et ajoute `.halo-pulse`), ou `null`. */
  haloCible: string | null;
  /** ▶ : au clic sur la boîte, à Entrée ou Espace (`BoiteDialogue.onSuite`). */
  avancer(): void;
  /** « Passer » — uniquement proposé sur une leçon bloquante (§2.1). */
  passer: (() => void) | null;
}

export function useMattChambre(): MattChambre | null {
  const run = useRunStore((s) => s.run);
  const state = run?.state ?? null;
  const runId = run?.runId ?? null;

  const actifReglage = useUIStore((s) => s.settings.decouverte);
  const dialogue = useUIStore((s) => s.dialog);
  const momentsSuspendus = useUIStore((s) => s.momentsSuspendus);
  const offreOuverte = useUIStore((s) => s.offreOuverte);
  const modaleOuverte = modaleEvenementOuverte(state?.pending_event ?? null, offreOuverte);
  const paliers = useRunStore((s) => s.paliersEnAttente.length);
  const chute = useRunStore((s) => s.chute);

  const courante = useDecouverteStore((s) => s.courante);
  const couranteBulle = useDecouverteStore((s) => s.couranteBulle);
  const vues = useDecouverteStore((s) => s.vues);
  const tourDeLaDerniere = useDecouverteStore((s) => s.tourDeLaDerniere);
  const synchroniser = useDecouverteStore((s) => s.synchroniser);
  const demander = useDecouverteStore((s) => s.demander);
  const fermer = useDecouverteStore((s) => s.fermer);
  const demanderBulle = useDecouverteStore((s) => s.demanderBulle);
  const fermerBulle = useDecouverteStore((s) => s.fermerBulle);
  const marquerVue = useDecouverteStore((s) => s.marquerVue);
  const dejaVue = useDecouverteStore((s) => s.dejaVue);

  // ⟦C27 · A-153⟧ Tant que l'accueil (`screens/Accueil/Accueil.tsx`) est à l'écran (les 4
  // répliques ET le glissement qui les suit), Matt se tait ICI : `accueilStore.terminer()` marque
  // au passage `briefing` vue (l'accueil a déjà dit ce qu'elle disait), donc rien ne la rejoue en
  // double dès que l'interface arrive. Voir plus bas pour la réplique qui prend sa place.
  const accueilActif = useAccueilStore((s) => s.actif);
  // ⚠️ **Bug trouvé en jouant, pas en lisant le code.** `Studio.tsx` (et donc ce hook) est monté
  // dès que `run` existe dans `runStore` — AVANT même que l'intro ne commence à jouer par-dessus
  // (`NouvelleRun.tsx` appelle `introStore.demarrer()` avant `creer()`, mais `run` n'apparaît que
  // quand `creer()` RÉSOUT : entre les deux, `Studio` est déjà là). Sans `!introActif` ici, ce
  // hook demandait `briefing` (bloquante, `quand: () => true`) PENDANT l'intro elle-même,
  // silencieusement (`Studio` ne rend rien à l'écran à ce stade) : `courante` restait posé sur
  // `briefing` pour de bon, et la vraie boîte l'affichait dès l'arrivée de l'interface — PAR-
  // DESSUS la réplique `apres_accueil` censée la remplacer, `accueilStore.marquerVue("briefing")`
  // arrivant beaucoup trop tard (au clic sur le glissement) pour empêcher `demander()` d'avoir
  // déjà eu lieu. `!introActif` couvre exactement cette fenêtre, comme `!accueilActif` couvre
  // celle d'après.
  const introActif = useIntroStore((s) => s.actif);

  const sfx = useSfx();

  // ⟦Consigne du 6/09, alerte immédiate⟧ Pas d'attente ici : `matchMedia` répond tout de suite,
  // et `resize` couvre le pilotage externe du viewport (même défaut documenté par
  // `BandeauPetitEcran` — les DevTools/l'automatisation ne déclenchent pas toujours `change`).
  const [etroit, setEtroit] = useState(false);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(REQUETE_PETIT_ECRAN);
    const maj = () => setEtroit(mq.matches);
    maj();
    mq.addEventListener("change", maj);
    window.addEventListener("resize", maj);
    return () => {
      mq.removeEventListener("change", maj);
      window.removeEventListener("resize", maj);
    };
  }, []);

  useEffect(() => {
    if (runId) synchroniser(runId);
  }, [runId, synchroniser]);

  // Le petit écran n'est pas une `Lecon` de `game/decouverte.ts` (son déclencheur est le
  // viewport, pas l'état) : `courante`/`couranteBulle` ne peuvent pas le porter, un état local
  // suffit — sa mémoire "vue" passe quand même par `decouverteStore.marquerVue`, la même liste
  // que tout le reste.
  const [petitEcranActif, setPetitEcranActif] = useState(false);

  // ⟦C27 · A-153⟧ La réplique qui prend la place de l'ancien briefing juste après l'accueil,
  // pas une `Lecon` de `game/decouverte.ts` non plus : son déclencheur est un MOMENT D'ÉCRAN (la
  // fin du glissement de l'interface), jamais un fait de l'état de la run, donc `paroleDue`
  // n'en sait rien (voir le commentaire d'`ID_APRES_ACCUEIL`, `game/parole.ts`, sur pourquoi ça
  // doit rester ainsi). Détectée ici sur le FRONT DESCENDANT d'`accueilActif` (vrai puis faux) :
  // une seule fois par accueil, jamais pour une run qui n'en a jamais eu un (`accueilActif`
  // reste alors tout le temps faux, le front ne se produit jamais).
  const [apresAccueilActif, setApresAccueilActif] = useState(false);
  const accueilActifPrecedent = useRef(accueilActif);
  useEffect(() => {
    const etaitActif = accueilActifPrecedent.current;
    accueilActifPrecedent.current = accueilActif;
    if (etaitActif && !accueilActif && !dejaVue(ID_APRES_ACCUEIL)) {
      setApresAccueilActif(true);
    }
  }, [accueilActif, dejaVue]);

  // ⟦C21 §2.2⟧ « elle se ferme au ▶ OU au premier clic ailleurs, comme aujourd'hui » — repris
  // tel quel d'`InfoBulleDecouverte` (`pointerdown` en capture, jamais `preventDefault` : le
  // clic continue son chemin normal vers ce qu'il visait, y compris un clic sur la boîte
  // elle-même, où `avancer()` referme de toute façon puisqu'une bulle n'a qu'une réplique).
  // `modaleOuverte` : même garde ⟦C16⟧ qu'avant — un événement qui s'ouvre pendant que la bulle
  // est là ne doit pas la fermer via CE clic, qui appartient à la modale.
  //
  // ⟦C29 · intégration · correctif tour 3⟧ **Étendu à `apres_accueil` et `petit_ecran`, plus
  // seulement `couranteBulle`.** Avant ce tour, ces deux répliques (états locaux à ce hook, pas
  // la mémoire de `decouverteStore`) ne se fermaient QUE par un clic direct sur la boîte
  // (`onSuite`/`avancer`, plus bas) : sur mobile, tant que `matt-mobile-bulle` restait
  // `pointer-events: none` (correctif tour 1), ce clic direct était impossible, `apres_accueil`
  // ne se marquait donc jamais vue et réapparaissait après chaque leçon bloquante qui la
  // masquait temporairement — le défaut trouvé par la vérification du tour 2. Le tour 3 retire
  // `pointer-events: none` (la boîte est repositionnée plutôt que traversée, `app.css`) ET
  // généralise cette fermeture "au premier tap ailleurs" aux trois surfaces non bloquantes
  // (`paroleNonBloquanteAffichee`, `game/parole.ts`, testée) : où qu'atterrisse le tap suivant,
  // sur le bouton visé ou sur la boîte elle-même, il referme la parole non bloquante en cours ET
  // la marque vue — jamais un « ailleurs » qui laisserait `apres_accueil`/`petit_ecran` bloqués
  // affichés pour de bon. Ne vaut toujours que pour le non-bloquant : une leçon bloquante
  // (`courante`) ne se ferme jamais que par « Passer » ou la dernière réplique, à dessein.
  useEffect(() => {
    const cible = paroleNonBloquanteAffichee(couranteBulle?.id ?? null, apresAccueilActif, petitEcranActif);
    if (!cible || modaleOuverte) return;
    const fermerAuProchainClic = () => {
      if (cible === ID_APRES_ACCUEIL) {
        marquerVue(ID_APRES_ACCUEIL);
        setApresAccueilActif(false);
      } else if (cible === ID_PETIT_ECRAN) {
        marquerVue(ID_PETIT_ECRAN);
        setPetitEcranActif(false);
      } else {
        fermerBulle();
      }
    };
    document.addEventListener("pointerdown", fermerAuProchainClic, true);
    return () => document.removeEventListener("pointerdown", fermerAuProchainClic, true);
  }, [couranteBulle, apresAccueilActif, petitEcranActif, modaleOuverte, fermerBulle, marquerVue]);

  // ⚠️ **Un seul « libre », mais PAS un seul garde** — bug trouvé et corrigé en session : une
  // première version ajoutait `!modaleOuverte` ici pour TOUTE parole, bloquante comprise. Or la
  // leçon bloquante `evenement` a `quand: pending_event !== null` — EXACTEMENT la condition qui
  // rend `modaleOuverte` vrai : avec le garde unique, elle ne pouvait donc jamais être demandée,
  // et `EvenementModal` (qui attend lui `courante === null`, `EvenementModal.tsx`) s'ouvrait
  // directement, sans jamais laisser Matt l'annoncer en premier — l'inverse exact de ce que
  // `game/decouverte.ts` documente depuis ⟦C16⟧ (« elle doit AU CONTRAIRE s'afficher PENDANT le
  // pending_event, avant que EvenementModal ne se montre »). Repéré en jouant une vraie
  // production jusqu'à un événement aléatoire. `modaleOuverte` ne doit donc gêner que les
  // paroles NON bloquantes (bulle, petit écran) — jamais une bloquante, exactement comme
  // `ModeDecouverte.libre` ne l'a jamais porté.
  //
  // ⟦C21 bis · point 3⟧ **Même défaut, chemin symétrique — trouvé par Matt en jouant au
  // clavier.** `libre` exigeait aussi `!couranteBulle` : si une info-bulle NON bloquante était
  // affichée (par ex. « energie », `s.energy < 100`) au moment où un `pending_event` apparaissait,
  // le garde unique empêchait de redemander `paroleDue` tant que la bulle restait là — `courante`
  // ne passait donc JAMAIS à `evenement`, et `EvenementModal` (qui n'attend QUE `courante !==
  // null`) s'ouvrait sans que Matt ait rien annoncé, exactement l'inverse de l'ordre documenté.
  // Corrigé : une leçon BLOQUANTE a désormais son propre garde, plus permissif — elle peut
  // interrompre une bulle ou le petit écran (elle les ferme au passage) ; seules une autre
  // bloquante déjà affichée (`courante`), un vrai dialogue, un moment suspendu ou un palier/une
  // chute en attente la retiennent encore. Le garde des paroles NON bloquantes ne change pas.
  // ⟦C27 · A-153⟧ `!accueilActif` en plus : l'accueil a sa propre boîte, sa propre voix, son
  // propre rythme, cette surface-ci doit rester muette tant qu'il tient l'écran, bloquante ou
  // non, sans quoi le briefing (`quand: () => true`, jamais vu tant que l'accueil ne l'a pas
  // marqué vu) se redemanderait PAR-DESSUS l'accueil dès que la chambre redevient « libre ».
  const libreBloquante =
    !courante && !dialogue && !momentsSuspendus && paliers === 0 && chute === null && !introActif && !accueilActif;
  const libre = libreBloquante && !couranteBulle && !petitEcranActif && !apresAccueilActif;

  useEffect(() => {
    if (!actifReglage || !state) return;
    if (!libreBloquante && !libre) return;
    const due = paroleDue(state, new Set(vues), tourDeLaDerniere, etroit);
    if (!due) return;
    if (due.bloquante) {
      // Une bloquante passe devant TOUT, y compris une modale de jeu qui n'a pas encore eu la
      // chance de s'ouvrir (`EvenementModal` attend `courante === null`) — jamais gênée par
      // `modaleOuverte` — et devant une bulle, le petit écran ou `apres_accueil` déjà affichés :
      // on les ferme pour lui laisser la main, plutôt que de la faire attendre indéfiniment.
      //
      // ⟦C29 · intégration · correctif tour 3⟧ `petitEcranActif`/`apresAccueilActif` sont
      // maintenant marqués VUS ici (`marquerVue`), comme `fermerBulle()` le fait déjà pour
      // `couranteBulle` juste au-dessus — avant ce tour, seul `petitEcranActif` était réinitialisé
      // (sans être marqué vu) et `apresAccueilActif` n'était même pas touché : les deux
      // pouvaient donc REPARAÎTRE une fois la bloquante refermée (`activeId` retombe dessus,
      // plus bas), exactement le défaut trouvé par la vérification du tour 2 (« pendant que tu
      // dors, la vie continue » vu deux fois). Une réplique non bloquante interrompue par une
      // bloquante est considérée dite, au même titre qu'une bulle interrompue — jamais redemandée.
      if (!libreBloquante) return;
      if (couranteBulle) fermerBulle();
      if (petitEcranActif) {
        marquerVue(ID_PETIT_ECRAN);
        setPetitEcranActif(false);
      }
      if (apresAccueilActif) {
        marquerVue(ID_APRES_ACCUEIL);
        setApresAccueilActif(false);
      }
      demander(due.id);
      return;
    }
    // Non bloquante : le jeu continue, mais pas question de la poser PAR-DESSUS une modale de
    // jeu déjà là (même garde ⟦C16⟧ qu'`InfoBulleDecouverte` avant ce chantier), ni par-dessus
    // une autre bulle/le petit écran déjà affichés (`libre`, inchangé).
    if (!libre) return;
    if (modaleOuverte) return;
    if (due.id === ID_PETIT_ECRAN) {
      setPetitEcranActif(true);
      return;
    }
    demanderBulle(due.id);
    // `vues`/`tourDeLaDerniere` exprès dans les dépendances : refermer une réplique doit
    // réévaluer s'il y en a une autre due tout de suite (même logique que `ModeDecouverte`).
  }, [
    actifReglage,
    state,
    libreBloquante,
    libre,
    modaleOuverte,
    couranteBulle,
    petitEcranActif,
    apresAccueilActif,
    vues,
    tourDeLaDerniere,
    etroit,
    demander,
    demanderBulle,
    fermerBulle,
    marquerVue,
  ]);

  const activeId: LeconId | null =
    courante?.id ??
    couranteBulle?.id ??
    (apresAccueilActif ? ID_APRES_ACCUEIL : null) ??
    (petitEcranActif ? ID_PETIT_ECRAN : null);
  const repliques = courante
    ? repliquesDe(courante)
    : couranteBulle
      ? repliquesDe(couranteBulle)
      : apresAccueilActif
        ? [REPLIQUE_APRES_ACCUEIL]
        : petitEcranActif
          ? [REPLIQUE_PETIT_ECRAN]
          : [];

  // ⟦Patron documenté de React : ajuster un état pendant le rendu⟧ plutôt qu'un `useEffect` —
  // deux `useState` (jamais un `ref` muté pendant le rendu, qui désynchroniserait un double
  // rendu de StrictMode) : dès que l'id actif change, l'index de réplique revient à 0 avant même
  // la première peinture, sans clignoter sur la dernière ligne de la réplique précédente.
  const [indexReplique, setIndexReplique] = useState(0);
  const [dernierIdVu, setDernierIdVu] = useState<LeconId | null>(null);
  if (activeId !== dernierIdVu) {
    setDernierIdVu(activeId);
    setIndexReplique(0);
  }

  useEffect(() => {
    if (activeId) sfx("dialog_open");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!activeId) return null;

  function fermerPourDeBon() {
    sfx("dialog_close");
    if (activeId === ID_PETIT_ECRAN) {
      marquerVue(ID_PETIT_ECRAN);
      setPetitEcranActif(false);
    } else if (activeId === ID_APRES_ACCUEIL) {
      marquerVue(ID_APRES_ACCUEIL);
      setApresAccueilActif(false);
    } else if (courante) {
      fermer(state?.turn ?? null);
    } else if (couranteBulle) {
      fermerBulle();
    }
  }

  function avancer() {
    if (indexReplique < repliques.length - 1) {
      setIndexReplique((i) => i + 1);
      return;
    }
    fermerPourDeBon();
  }

  return {
    actif: true,
    bloquante: !!courante,
    texte: repliques[indexReplique] ?? "",
    haloCible: haloDeReplique(activeId, indexReplique),
    avancer,
    passer: courante ? fermerPourDeBon : null,
  };
}
